import type { Seguimiento, Surgery } from '@/lib/types';

/**
 * ListMine returns summary payloads (no pasos/ficha/full notes). A record is
 * "thin" when it still needs surgeries.getById before edit/detail/export.
 */
export function isSurgeryDetailThin(s: Surgery | undefined | null): boolean {
  if (!s) return true;
  const hasNote = Boolean(s.descripcion?.trim() && s.descripcion.trim().length > 240);
  const hasPasos = Boolean(s.pasos?.some((p) => p.trim()));
  const hasFicha = Boolean(s.ficha);
  const hasHallazgos = Boolean(s.hallazgos?.trim());
  // Summaries clip descripcion to ≤240 chars and omit pasos/ficha/hallazgos.
  // Local full records (or getById) have at least one of these.
  if (hasPasos || hasFicha || hasHallazgos || hasNote) return false;
  // Brand-new empty draft: treat as loaded so we don't spin forever.
  if (!s.descripcion?.trim() && !(s.materiales?.length ?? 0) && !s.lecciones?.trim()) {
    return false;
  }
  // Summary-shaped: short descripcion clip and/or materials only.
  return true;
}

/**
 * listMine summaries keep only diasHospitalizacion / proximaRevision on
 * seguimiento — never complicacionesLargoPlazo. Consumers (FollowUpTimeline,
 * hospitalización hints, ficha) assume the array exists; normalize at the
 * boundary so thin rows never throw during render.
 */
export function normalizeSeguimiento(
  s: Partial<Seguimiento> | undefined | null,
): Seguimiento | undefined {
  if (!s) return undefined;
  const hasAny =
    typeof s.diasHospitalizacion === 'number' ||
    typeof s.proximaRevision === 'string' ||
    Boolean(s.anatomiaPatologica) ||
    Boolean(s.notasSeguimiento) ||
    Boolean(s.complicacionesLargoPlazoNota) ||
    (Array.isArray(s.complicacionesLargoPlazo) && s.complicacionesLargoPlazo.length > 0) ||
    // Keep an empty object that only had the required array (full docs).
    Array.isArray(s.complicacionesLargoPlazo);
  if (!hasAny) return undefined;
  return {
    ...s,
    complicacionesLargoPlazo: Array.isArray(s.complicacionesLargoPlazo)
      ? s.complicacionesLargoPlazo
      : [],
  };
}

/** Ensure arrays/strings the UI reads without optional chaining are present. */
export function normalizeSurgeryShape(s: Surgery): Surgery {
  return {
    ...s,
    materiales: Array.isArray(s.materiales) ? s.materiales : [],
    lecciones: typeof s.lecciones === 'string' ? s.lecciones : '',
    seguimiento: normalizeSeguimiento(s.seguimiento),
    complicaciones:
      s.complicaciones && typeof s.complicaciones === 'object'
        ? s.complicaciones
        : { hubo: false },
  };
}

/** Merge a server summary into a local full record without wiping detail. */
export function mergeSurgerySummary(local: Surgery | undefined, summary: Surgery): Surgery {
  const remote = normalizeSurgeryShape(summary);
  if (!local) return remote;
  const localFull = normalizeSurgeryShape(local);
  const localNewer =
    Date.parse(localFull.actualizadoEn) > Date.parse(remote.actualizadoEn);
  if (localNewer && !isSurgeryDetailThin(localFull)) {
    return {
      ...remote,
      descripcion: localFull.descripcion,
      pasos: localFull.pasos,
      hallazgos: localFull.hallazgos,
      materiales: localFull.materiales.length ? localFull.materiales : remote.materiales,
      lecciones: localFull.lecciones || remote.lecciones,
      ficha: localFull.ficha,
      seguimiento: normalizeSeguimiento(
        localFull.seguimiento
          ? {
              ...remote.seguimiento,
              ...localFull.seguimiento,
            }
          : remote.seguimiento,
      ),
      actualizadoEn: localFull.actualizadoEn,
    };
  }
  // Prefer local heavy fields when summary omitted them.
  return {
    ...remote,
    descripcion:
      remote.descripcion && remote.descripcion.length >= (localFull.descripcion?.length ?? 0)
        ? remote.descripcion
        : localFull.descripcion || remote.descripcion,
    pasos: localFull.pasos?.length ? localFull.pasos : remote.pasos,
    hallazgos: localFull.hallazgos || remote.hallazgos,
    materiales: remote.materiales.length ? remote.materiales : localFull.materiales,
    lecciones:
      (localFull.lecciones?.length ?? 0) > (remote.lecciones?.length ?? 0)
        ? localFull.lecciones
        : remote.lecciones,
    ficha: localFull.ficha ?? remote.ficha,
    seguimiento: normalizeSeguimiento(
      localFull.seguimiento
        ? {
            ...remote.seguimiento,
            ...localFull.seguimiento,
            ...(remote.seguimiento?.proximaRevision
              ? { proximaRevision: remote.seguimiento.proximaRevision }
              : {}),
            ...(typeof remote.seguimiento?.diasHospitalizacion === 'number'
              ? { diasHospitalizacion: remote.seguimiento.diasHospitalizacion }
              : {}),
          }
        : remote.seguimiento,
    ),
  };
}
